import React, { useMemo, useState } from 'react';
import BrandHeader from '../components/BrandHeader';
import { getBrandConfig, getCurrentBrand } from '../brand-config';
import AutomationCard from '../components/AutomationCard';
import AutomationDetail from '../components/AutomationDetail';
import { getAutomationsForBrand, getAutomationCategories } from '../data/automationLibrary';
import type { Automation } from '../data/automationLibrary';
import '../styles/automation-library.css';

const AutomationLibrary: React.FC = () => {
  const brandConfig = getBrandConfig();
  const currentBrand = useMemo(() => getCurrentBrand(), []);
  const brandToken = currentBrand === 'marsmen' ? 'mars-men' : currentBrand === 'hollow' ? 'hollow-socks' : null;
  const [selectedAutomation, setSelectedAutomation] = useState<Automation | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const rawAutomations = useMemo(
    () => getAutomationsForBrand(currentBrand),
    [currentBrand],
  );

  const automations = useMemo(() => {
    return rawAutomations.map((automation) => {
      if (brandToken === null) {
        return automation;
      }

      const filteredBrands = automation.brands.filter((brand) =>
        brand === brandToken || brand === 'all' || brand === 'easy-street'
      );

      return {
        ...automation,
        brands: filteredBrands.length > 0 ? filteredBrands : [brandToken],
      };
    });
  }, [rawAutomations, brandToken]);

  const categories = useMemo(
    () => ['all', ...getAutomationCategories(currentBrand)],
    [currentBrand],
  );

  const statuses = ['all', 'active', 'paused', 'development', 'error'];

  const filteredAutomations = useMemo(() => {
    return automations.filter((automation) => {
      const categoryMatch = filterCategory === 'all' || automation.category === filterCategory;
      const statusMatch = filterStatus === 'all' || automation.status === filterStatus;
      return categoryMatch && statusMatch;
    });
  }, [automations, filterCategory, filterStatus]);

  const statusCounts = useMemo(() => {
    return {
      active: automations.filter((automation) => automation.status === 'active').length,
      paused: automations.filter((automation) => automation.status === 'paused').length,
      development: automations.filter((automation) => automation.status === 'development').length,
    };
  }, [automations]);


  return (
    <div
      className="app automation-app"
      style={{ '--primary-color': brandConfig.primaryColor } as React.CSSProperties}
    >
      <BrandHeader />

  <div className="automation-library">
        <main className="automation-main">
          <div className="automation-container">
            <div className="page-header">
              <h1>{brandConfig.name} Automation Library</h1>
              <p>Manage and monitor automation workflows powering {brandConfig.name}</p>
            </div>

          {!selectedAutomation ? (
            <>
              <div className="filters-bar">
                <div className="filter-group">
                  <label>Category:</label>
                  <select 
                    value={filterCategory} 
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="filter-select"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat === 'all' ? 'All Categories' : cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Status:</label>
                  <select 
                    value={filterStatus} 
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select"
                  >
                    {statuses.map(status => (
                      <option key={status} value={status}>
                        {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="automation-stats">
                  <span className="stat-badge">
                    <span className="stat-number">{statusCounts.active}</span> Active
                  </span>
                  <span className="stat-badge">
                    <span className="stat-number">{statusCounts.paused}</span> Paused
                  </span>
                  <span className="stat-badge">
                    <span className="stat-number">{statusCounts.development}</span> In Development
                  </span>
                </div>
              </div>

              {filteredAutomations.length > 0 ? (
                <div className="automations-grid">
                  {filteredAutomations.map(automation => (
                    <AutomationCard
                      key={automation.id}
                      automation={automation}
                      onClick={() => setSelectedAutomation(automation)}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <h3>No automations match your filters</h3>
                  <p>
                    Try adjusting the category or status filters to explore other workflows available to {brandConfig.name}.
                  </p>
                </div>
              )}
            </>
          ) : (
            <AutomationDetail
              automation={selectedAutomation}
              onBack={() => setSelectedAutomation(null)}
            />
          )}
        </div>
        </main>
      </div>
    </div>
  );
};

export default AutomationLibrary;
